/** Business template types used by the invoice template customizer */
export const BusinessTemplateType = {
  INVOICE: 2,
  JOBCARD: 3,
  WORKORDER: 4,
  INSPECTION: 5,
  DELIVERY_NOTE: 6,
  PROFORMA_INVOICE: 7,
} as const;

export const isProformaInvoiceTemplateType = (templateType?: number) =>
  templateType === BusinessTemplateType.PROFORMA_INVOICE;

export const isInvoiceOrProformaTemplateType = (templateType?: number) =>
  templateType === BusinessTemplateType.INVOICE || templateType === BusinessTemplateType.PROFORMA_INVOICE;

/** INVOICE, JOBCARD, WORKORDER, DELIVERY NOTE */
export const STANDARD_BUSINESS_SECTION_ORDER = [
  'logo',
  'header',
  'vehicleInfo',
  'table',
  'summary',
  'terms',
  'notes',
  'footer',
] as const;

/** Inspection */
export const INSPECTION_SECTION_ORDER = [
  'logo',
  'header',
  'vehicleInfo',
  'inspectionItems',
  'inspectionRemarks',
  'customerSignature',
  'terms',
  'notes',
  'footer',
] as const;

/** Proforma invoice */
export const PROFORMA_SECTION_ORDER = ['logo', 'header', 'table', 'summary', 'terms', 'notes', 'footer'] as const;

/** Canonical layout section order when switching template type in the customizer */
export const getDefaultSectionOrderForTemplateType = (templateType: number): string[] => {
  if (templateType === BusinessTemplateType.INSPECTION) {
    return [...INSPECTION_SECTION_ORDER];
  }

  if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
    return [...PROFORMA_SECTION_ORDER];
  }

  return [...STANDARD_BUSINESS_SECTION_ORDER];
};

// CustomSettings interface matching POS structure
export interface CustomSettings {
  // General Settings
  languageDisplay?: 'both' | 'primary' | 'secondary';
  dateFormat?: string;
  invoiceType?: 'normal' | 'consolidated'; // Invoice type: normal or consolidated
  /** Invoice print layout: standard A4-style or thermal receipt */
  printFormat?: 'normal' | 'thermal';
  /** Thermal paper width preset (mm) */
  thermalPaperWidthPreset?: '112' | '80' | '58' | 'other';
  /** Custom thermal width in mm when preset is "other" (max 210) */
  thermalCustomWidthMm?: number;
  showLogo?: boolean;
  showHeader?: boolean;
  showVehicleInfo?: boolean;
  showTable?: boolean;
  showTerms?: boolean;
  showNotes?: boolean;

  // Logo Settings
  logoPosition?: 'left' | 'center' | 'right' | 'none';
  logoWidth?: number;
  logoHeight?: number;
  useDefaultLogoSize?: boolean;
  /** When true, logo spans full printable page/content width (ignores logoWidth) */
  logoFitToWidth?: boolean;
  showQR?: boolean;
  // Independent positioning for logo, invoice number, and QR
  invoiceNumberPosition?: 'left' | 'center' | 'right' | 'none';
  qrPosition?: 'left' | 'center' | 'right' | 'none';
  showInvoiceNumber?: boolean;
  invoiceNumberGrouping?: 'independent' | 'withLogo' | 'withQR' | 'withBoth';
  invoiceNumberFontSize?: number;
  invoiceNumberFontWeight?: number | string;
  // Jobcard fields
  showJobcardNumber?: boolean;
  jobcardNumberPosition?: 'left' | 'center' | 'right' | 'none';
  jobcardNumberFontSize?: number;
  jobcardNumberFontWeight?: number | string;
  jobcardHeadingPrimary?: string;
  jobcardHeadingSecondary?: string;
  // Workorder fields
  showWorkorderNumber?: boolean;
  workorderNumberPosition?: 'left' | 'center' | 'right' | 'none';
  workorderNumberFontSize?: number;
  workorderNumberFontWeight?: number | string;
  workorderHeadingPrimary?: string;
  workorderHeadingSecondary?: string;
  // Delivery note fields
  showDeliveryNoteNumber?: boolean;
  deliveryNoteNumberPosition?: 'left' | 'center' | 'right' | 'none';
  deliveryNoteNumberFontSize?: number;
  deliveryNoteNumberFontWeight?: number | string;
  deliveryNoteHeadingPrimary?: string;
  deliveryNoteHeadingSecondary?: string;
  // Proforma invoice fields
  showProformaInvoiceNumber?: boolean;
  proformaInvoiceNumberPosition?: 'left' | 'center' | 'right' | 'none';
  proformaInvoiceNumberFontSize?: number;
  proformaInvoiceNumberFontWeight?: number | string;
  proformaInvoiceHeadingPrimary?: string;
  proformaInvoiceHeadingSecondary?: string;

  // Header Settings
  showCompanySection?: boolean;
  showCustomerSection?: boolean;
  invoiceHeadingPrimary?: string;
  invoiceHeadingSecondary?: string;
  showInvoiceDirection?: string;
  companyFields?: {
    name?: boolean;
    vat?: boolean;
    phone?: boolean;
    email?: boolean;
    address?: boolean;
  };
  customerFields?: {
    name?: boolean;
    phone?: boolean;
    email?: boolean;
    address?: boolean;
    vat?: boolean;
  };
  companyLabels?: {
    name?: { primary?: string; secondary?: string };
    vat?: { primary?: string; secondary?: string };
    phone?: { primary?: string; secondary?: string };
    email?: { primary?: string; secondary?: string };
    address?: { primary?: string; secondary?: string };
  };
  customerLabels?: {
    name?: { primary?: string; secondary?: string };
    phone?: { primary?: string; secondary?: string };
    email?: { primary?: string; secondary?: string };
    address?: { primary?: string; secondary?: string };
    vat?: { primary?: string; secondary?: string };
  };

  // Vehicle Info Settings
  vehicleFields?: {
    color?: boolean;
    fuelType?: boolean;
    vin?: boolean;
    mileage?: boolean;
    make?: boolean;
    model?: boolean;
    year?: boolean;
    plateNumber?: boolean;
  };
  vehicleLabels?: {
    color?: { primary?: string; secondary?: string };
    fuelType?: { primary?: string; secondary?: string };
    vin?: { primary?: string; secondary?: string };
    mileage?: { primary?: string; secondary?: string };
    make?: { primary?: string; secondary?: string };
    model?: { primary?: string; secondary?: string };
    year?: { primary?: string; secondary?: string };
    plateNumber?: { primary?: string; secondary?: string };
  };

  // Table Settings
  tableLayout?: 'combined' | 'separate';
  showPackages?: boolean;
  showServices?: boolean;
  showItems?: boolean;
  descriptionDisplay?: 'all' | 'title' | 'code' | 'description'; // Deprecated - use itemDisplayOptions instead
  itemDisplayOptions?: {
    showTitle?: boolean;
    showCode?: boolean;
    showDescription?: boolean;
  };
  tableColumns?: {
    serialNo?: boolean;
    item?: boolean;
    description?: boolean;
    quantity?: boolean;
    unitPrice?: boolean;
    discount?: boolean;
    discountedSubtotal?: boolean;
    tax?: boolean;
    totalWithTax?: boolean;
    amount?: boolean;
  };
  tableColumnLabels?: {
    serialNo?: { primary?: string; secondary?: string };
    item?: { primary?: string; secondary?: string };
    description?: { primary?: string; secondary?: string };
    quantity?: { primary?: string; secondary?: string };
    unitPrice?: { primary?: string; secondary?: string };
    discountedSubtotal?: { primary?: string; secondary?: string };
    tax?: { primary?: string; secondary?: string };
    totalWithTax?: { primary?: string; secondary?: string };
    amount?: { primary?: string; secondary?: string };
  };

  // Consolidated Invoice Table Settings
  consolidatedTableColumns?: {
    serialNo?: boolean;
    vehicleName?: boolean;
    vehiclePlateNo?: boolean;
    deliveryNoteNo?: boolean;
    subtotal?: boolean;
    discount?: boolean;
    discountedSubtotal?: boolean;
    tax?: boolean;
    totalWithTax?: boolean;
    total?: boolean;
  };
  consolidatedTableColumnLabels?: {
    serialNo?: { primary?: string; secondary?: string };
    vehicleName?: { primary?: string; secondary?: string };
    vehiclePlateNo?: { primary?: string; secondary?: string };
    deliveryNoteNo?: { primary?: string; secondary?: string };
    subtotal?: { primary?: string; secondary?: string };
    discount?: { primary?: string; secondary?: string };
    discountedSubtotal?: { primary?: string; secondary?: string };
    tax?: { primary?: string; secondary?: string };
    totalWithTax?: { primary?: string; secondary?: string };
    total?: { primary?: string; secondary?: string };
  };

  // Summary & Footer Settings
  summaryItems?: {
    subtotal?: boolean;
    discount?: boolean;
    discountedSubtotal?: boolean;
    tax?: boolean;
    total?: boolean;
  };
  summaryLabels?: {
    subtotal?: { primary?: string; secondary?: string };
    discount?: { primary?: string; secondary?: string };
    discountedSubtotal?: { primary?: string; secondary?: string };
    tax?: { primary?: string; secondary?: string };
    total?: { primary?: string; secondary?: string };
    creditAmount?: { primary?: string; secondary?: string };
    paidAmount?: { primary?: string; secondary?: string };
    dueAmount?: { primary?: string; secondary?: string };
  };
  showPaymentInfo?: boolean;
  showFooter?: boolean;
  footerText?: string;
  footerItems?: {
    address?: boolean;
    email?: boolean;
    phone?: boolean;
    socialLinks?: boolean;
    pageNumber?: boolean;
  };
  paymentLabels?: {
    primary?: string;
    secondary?: string;
  };

  // Inspection Template Settings
  showInspectionItems?: boolean;
  inspectionItems?: Array<{
    id?: string;
    partName?: string;
    status?: 'working' | 'needRepair' | 'brokenOrMissing';
    notes?: string;
  }>;
  showVehicleImage?: boolean;
  vehicleImagePosition?: 'left' | 'right' | 'center' | 'none';
  showCustomerSignature?: boolean;
  customerSignatureLabel?: { primary?: string; secondary?: string };
  showInspectionRemarks?: boolean;
  inspectionRemarksLabel?: { primary?: string; secondary?: string };
  showTechnicians?: boolean;
  inspectionTechniciansLabel?: { primary?: string; secondary?: string };

  // Layout Order (for drag-and-drop)
  sectionOrder?: string[];

  /**
   * UI / preview design key hydrated from the template `designVariant` column.
   * Persist as top-level column integers: CLASSIC = 1, MODERN = 2 (not in attributes).
   * Legacy attributes may still contain "classic" | "modern" strings — map via helpers.
   */
  designVariant?: 'classic' | 'modern';
}

/** Backend column values for business template designVariant */
export const DesignVariantColumn = {
  CLASSIC: 1,
  MODERN: 2,
} as const;

export type DesignVariantKey = 'classic' | 'modern';

/** Map API column (1|2) or legacy string to UI key */
export const designVariantToKey = (
  value?: number | string | null,
): DesignVariantKey => {
  if (value === DesignVariantColumn.MODERN || value === '2' || value === 'modern') {
    return 'modern';
  }
  return 'classic';
};

/** Map UI key / legacy value to API column integer */
export const designVariantToColumn = (
  value?: number | string | null,
): number => {
  if (value === DesignVariantColumn.MODERN || value === '2' || value === 'modern') {
    return DesignVariantColumn.MODERN;
  }
  return DesignVariantColumn.CLASSIC;
};

/**
 * Merge top-level `designVariant` column into settings for preview/customizer.
 * Prefers the column value; falls back to nested attributes for legacy templates.
 */
export const withDesignVariantInSettings = (
  settings: CustomSettings | undefined | null,
  designVariantColumn?: number | string | null,
): CustomSettings => {
  const base = { ...(settings || {}) } as CustomSettings;
  const hasColumn =
    designVariantColumn !== undefined &&
    designVariantColumn !== null &&
    designVariantColumn !== '';
  return {
    ...base,
    designVariant: hasColumn
      ? designVariantToKey(designVariantColumn)
      : designVariantToKey(base.designVariant as number | string | null | undefined),
  };
};

/** Remove designVariant from attributes payload — it is stored as its own column */
export const stripDesignVariantFromAttributes = (
  settings: CustomSettings | undefined | null,
): CustomSettings => {
  if (!settings) return {};
  const { designVariant: _removed, ...rest } = settings;
  return rest;
};

export interface InvoiceTemplateData {
  id?: number;
  name: string;
  description?: string;
  isDefault?: boolean;
  settings?: CustomSettings;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
  status?: number;
  data?: unknown;
}

/** Backfill dedicated proforma fields from legacy invoice-field storage */
export const migrateProformaSettingsFromLegacy = (settings: CustomSettings): Partial<CustomSettings> => {
  if (settings.showProformaInvoiceNumber !== undefined) {
    return {};
  }

  return {
    showProformaInvoiceNumber: settings.showInvoiceNumber ?? true,
    proformaInvoiceNumberPosition: settings.invoiceNumberPosition ?? 'left',
    proformaInvoiceNumberFontSize: settings.invoiceNumberFontSize ?? 18,
    proformaInvoiceNumberFontWeight: settings.invoiceNumberFontWeight ?? 700,
    proformaInvoiceHeadingPrimary: settings.invoiceHeadingPrimary ?? 'PROFORMA INVOICE',
    proformaInvoiceHeadingSecondary: settings.invoiceHeadingSecondary ?? '',
  };
};

export const isDefaultBusinessTemplate = (template?: {
  default?: boolean;
  isDefault?: boolean;
} | null) => template?.default === true || template?.isDefault === true;

/** Company + customer header used by invoice/JC/WO, applied only to the default proforma template */
export const DEFAULT_PROFORMA_HEADER_DETAILS: Pick<
  CustomSettings,
  'showCompanySection' | 'showCustomerSection' | 'companyFields' | 'customerFields'
> = {
  showCompanySection: true,
  showCustomerSection: true,
  companyFields: {
    name: true,
    email: true,
    phone: true,
    address: true,
    vat: false,
  },
  customerFields: {
    name: true,
    email: true,
    phone: true,
    address: true,
    vat: false,
  },
};

export const withDefaultProformaHeaderDetails = (
  settings: CustomSettings,
  templateType?: number,
  isDefault?: boolean,
): CustomSettings => {
  if (!isProformaInvoiceTemplateType(Number(templateType)) || !isDefault) {
    return settings;
  }

  return {
    ...settings,
    ...DEFAULT_PROFORMA_HEADER_DETAILS,
  };
};
